import { useWealthOverview } from '@egw/api'
import { WebpGreenGradientSplash } from '@egw/assets'
import { Button, ButtonVariant, HeaderCover, ModalDrawer, Popover } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { RecentActivity } from '@egw/features-transactions'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { Currency, getCurrencySymbol } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { SavingsOverflowMenu } from '../../components/savings-overflow-menu/savings-overflow-menu'

const log = new Logger('Savings Account Page')

export interface SavingsAccountPageProps {
  alias: number
}

export const SavingsAccountPage: FC<SavingsAccountPageProps> = ({ alias }) => {
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const { t } = useTranslation('features-account')
  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error } = useWealthOverview()

  const data = wealthOverviewData?.highYieldSavings?.accounts.find((account) => account.alias === alias)

  const totalBalanceCurrencyCode = data?.balance.total.currencyCode
  const totalBalanceAmount = data?.balance.total.amount
  const availableBalanceCurrencyCode = data?.balance.available.currencyCode
  const availableBalanceAmount = data?.balance.available.amount
  const apy = data?.apy?.toFixed(2) ?? 0

  useEffect(() => {
    // If WealthOverview Data has returned and there are no accounts, navigate to the overview page
    if (wealthOverviewData && data === undefined) {
      log.info('No savings account found, navigating to overview page')
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    }
  }, [data, wealthOverviewData, navigate])

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {data ? (
        <>
          <HeaderCover
            category={wealthOverviewData?.highYieldSavings?.productName?.toUpperCase() ?? ''}
            title={data.accountName}
            imgUrl={WebpGreenGradientSplash}
            onClick={() => {
              setShowOverflowMenu(true)
            }}
          />
          <div className="egw-container -mt-12">
            <div className="bg-white -mt-7 md:-mt-12 rounded-xl p-4 shadow-lg flex flex-col gap-3 mb-10">
              <h3 className="text-2xl lg:text-3xl font-light  text-header leading-8 md:leading-10">
                {t('account_savings.total_balance')}
              </h3>
              <p className="text-3xl md:text-3xl leading-[2.2rem] font-semibold text-header">
                {totalBalanceCurrencyCode ? <span>{getCurrencySymbol(totalBalanceCurrencyCode)}</span> : null}
                <span>
                  {totalBalanceAmount
                    ? Currency.integerCents.onlyMinus(totalBalanceAmount)
                    : Currency.integerCents.onlyMinus(0)}
                </span>
              </p>
              <div className="flex font-light text-lg text-meta">
                <p className="flex-1">{t('account_savings.available_balance')}</p>
                <div>
                  {availableBalanceCurrencyCode ? <span>{getCurrencySymbol(availableBalanceCurrencyCode)}</span> : null}
                  <span>
                    {availableBalanceAmount
                      ? Currency.integerCents.onlyMinus(availableBalanceAmount)
                      : Currency.integerCents.onlyMinus(0)}
                  </span>
                </div>
              </div>
              {apy ? (
                <>
                  <hr className="opacity-50" />
                  <div className="flex font-light text-lg text-body flex flex-row justify-between">
                    <div className="flex flex-row gap-1 items-center">
                      <p className="flex-1">{t('checking.apy_label')}</p>
                      <Popover iconClassnames="text-body" title={t('default.savings.apy_title')}>
                        {t('default.savings.apy_description', { apy })}
                      </Popover>
                    </div>
                    <p>{t('checking.apy', { apy })}</p>
                  </div>
                </>
              ) : null}
            </div>

            <div className="flex gap-6">
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  className="w-full py-4"
                  onClick={() => {
                    sendEvent('user_savingsTransfer_click', {
                      eventSource: 'egWealth',
                      category: 'transfer',
                      subCategory: 'intent',
                    })
                    void navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                >
                  {t('account_transfer.transfer')}
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  className="w-full py-4"
                  onClick={() => {
                    void navigate(WEALTH_ROUTES.SAVINGS_ACCOUNT_DETAILS.path.replace(':alias', alias.toString()))
                  }}
                >
                  {t('account_transfer.account_details')}
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {error ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('account_savings.error')}</p>
        </div>
      ) : null}

      {data?.alias !== undefined ? <RecentActivity alias={data.alias} /> : null}

      <ModalDrawer
        title={t('default.account_options_label')}
        isOpen={showOverflowMenu}
        onClose={() => {
          setShowOverflowMenu(false)
        }}
      >
        <SavingsOverflowMenu alias={alias} />
      </ModalDrawer>
    </>
  )
}
